import * as React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import type QuillType from "quill";
import "quill/dist/quill.snow.css";

interface WysiwygEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label: string;
  error?: string;
  required?: boolean;
}

/**
 * Unified WYSIWYG Editor Component
 * 
 * Features:
 * - Lazy loads Quill only when needed
 * - Proper cleanup on unmount
 * - Handles race conditions during initialization
 * - Syncs between Visual and HTML modes
 * - Normalizes empty content consistently
 */
export function WysiwygEditorUnified({
  value,
  onChange,
  placeholder = "Start writing...",
  label,
  error,
  required = false,
}: WysiwygEditorProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const quillRef = React.useRef<QuillType | null>(null);
  const quillModuleRef = React.useRef<typeof QuillType | null>(null);
  const toolbarRef = React.useRef<HTMLElement | null>(null);
  const isInitializingRef = React.useRef(false);
  const isUpdatingFromPropsRef = React.useRef(false);
  const lastSyncedValueRef = React.useRef<string>(value || "");
  const isToolbarStickyRef = React.useRef(false);
  
  const [activeTab, setActiveTab] = React.useState<"visual" | "html">("visual");
  const [htmlContent, setHtmlContent] = React.useState(value || "");
  const [isQuillReady, setIsQuillReady] = React.useState(false);
  const [initError, setInitError] = React.useState<string | null>(null);

  // Normalize HTML - empty content becomes ""
  const normalizeHtml = React.useCallback((html: string): string => {
    if (!html || html.trim() === "") {
      return "";
    }
    // Convert Quill's default empty paragraph to truly empty
    if (html === "<p><br></p>" || html === "<p></p>") {
      return "";
    }
    return html;
  }, []);

  // Load Quill module (cached after first load)
  const loadQuillModule = React.useCallback(async (): Promise<typeof QuillType> => {
    if (quillModuleRef.current) {
      return quillModuleRef.current;
    }

    try {
      const quillModule = await import("quill");
      const Quill = quillModule.default ?? (quillModule as unknown as typeof QuillType);
      quillModuleRef.current = Quill;
      return Quill;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load Quill editor";
      setInitError(errorMessage);
      throw error;
    }
  }, []);

  // Initialize Quill editor
  const initializeQuill = React.useCallback(async () => {
    // Prevent multiple initializations
    if (quillRef.current || !containerRef.current || isInitializingRef.current) {
      return;
    }

    // Only initialize when visual tab is active
    if (activeTab !== "visual") {
      return;
    }

    try {
      isInitializingRef.current = true;
      setInitError(null);

      // Clear container to prevent duplicate toolbars
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }

      // Load Quill module
      const Quill = await loadQuillModule();

      // Double-check container still exists and Quill not already initialized
      if (!containerRef.current || quillRef.current) {
        isInitializingRef.current = false;
        return;
      }

      // Create Quill instance
      quillRef.current = new Quill(containerRef.current, {
        theme: "snow",
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, 4, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["blockquote", "code-block"],
            [{ align: [] }],
            ["link", "image"],
            ["clean"],
          ],
        },
        placeholder,
      });

      // Set minimum height
      const editorEl = containerRef.current.querySelector(".ql-editor") as HTMLElement | null;
      if (editorEl) {
        editorEl.style.minHeight = "400px";
      }

      // Setup toolbar sticky positioning after a short delay to ensure DOM is ready
      setTimeout(() => {
        const toolbarEl = containerRef.current?.querySelector(".ql-toolbar") as HTMLElement | null;
        if (toolbarEl && !toolbarRef.current) {
          toolbarRef.current = toolbarEl;
          
          // Apply sticky styles
          toolbarEl.style.position = "sticky";
          toolbarEl.style.top = "0";
          toolbarEl.style.zIndex = "50";
          toolbarEl.style.backgroundColor = "#fff";
          toolbarEl.style.borderBottom = "1px solid #e5e7eb";
          toolbarEl.style.paddingTop = "8px";
          toolbarEl.style.paddingBottom = "8px";
          toolbarEl.style.marginBottom = "0";
          toolbarEl.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
          toolbarEl.style.transition = "box-shadow 0.2s ease";
          
          // Ensure container and parent containers allow sticky positioning
          let currentElement: HTMLElement | null = containerRef.current;
          while (currentElement) {
            const computedStyle = window.getComputedStyle(currentElement);
            // Check for overflow that would break sticky
            if (computedStyle.overflow === "hidden" || computedStyle.overflowY === "hidden") {
              currentElement.style.overflowY = "visible";
            }
            // Check parent
            currentElement = currentElement.parentElement;
            // Stop at body or html
            if (currentElement?.tagName === "BODY" || currentElement?.tagName === "HTML") {
              break;
            }
          }
        }
      }, 100);

      // Set initial content
      const initialContent = normalizeHtml(value || htmlContent || "");
      if (initialContent && quillRef.current) {
        isUpdatingFromPropsRef.current = true;
        const delta = quillRef.current.clipboard.convert({ html: initialContent });
        quillRef.current.setContents(delta, "silent");
        isUpdatingFromPropsRef.current = false;
        lastSyncedValueRef.current = initialContent;
      }

      // Listen for text changes
      quillRef.current.on("text-change", () => {
        if (!quillRef.current || isUpdatingFromPropsRef.current) {
          return;
        }

        const html = quillRef.current.root.innerHTML;
        const normalized = normalizeHtml(html);
        
        // Only update if content actually changed
        if (normalized !== lastSyncedValueRef.current) {
          lastSyncedValueRef.current = normalized;
          setHtmlContent(normalized);
          onChange(normalized);
        }
      });

      setIsQuillReady(true);
    } catch (error) {
      console.error("Failed to initialize Quill:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to initialize editor";
      setInitError(errorMessage);
    } finally {
      isInitializingRef.current = false;
    }
  }, [activeTab, value, htmlContent, placeholder, onChange, normalizeHtml, loadQuillModule]);

  // Initialize Quill when visual tab becomes active
  React.useEffect(() => {
    if (activeTab === "visual" && !quillRef.current && !isInitializingRef.current) {
      initializeQuill();
    }
  }, [activeTab, initializeQuill]);

  // Handle scroll to make toolbar fixed at viewport top when scrolled past
  React.useEffect(() => {
    if (!isQuillReady || activeTab !== "visual") {
      return;
    }

    // Function to setup scroll handler
    const setupScrollHandler = (toolbarEl: HTMLElement, containerEl: HTMLElement) => {
      let rafId: number | null = null;

      const updateToolbarPosition = () => {
        if (!toolbarEl || !containerEl) return;
        
        const containerRect = containerEl.getBoundingClientRect();
        const toolbarRect = toolbarEl.getBoundingClientRect();
        
        // If toolbar is scrolled past the top of viewport, make it fixed
        if (toolbarRect.top <= 0 && containerRect.top < 0) {
          if (!isToolbarStickyRef.current) {
            isToolbarStickyRef.current = true;
            const containerComputedStyle = window.getComputedStyle(containerEl);
            const containerPaddingLeft = parseInt(containerComputedStyle.paddingLeft) || 0;
            const containerPaddingRight = parseInt(containerComputedStyle.paddingRight) || 0;
            
            toolbarEl.style.position = "fixed";
            toolbarEl.style.top = "0";
            toolbarEl.style.left = `${containerRect.left + containerPaddingLeft}px`;
            toolbarEl.style.width = `${containerRect.width - containerPaddingLeft - containerPaddingRight}px`;
            toolbarEl.style.zIndex = "100";
            toolbarEl.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
          }
        } else {
          if (isToolbarStickyRef.current) {
            isToolbarStickyRef.current = false;
            toolbarEl.style.position = "sticky";
            toolbarEl.style.top = "0";
            toolbarEl.style.left = "";
            toolbarEl.style.width = "";
            toolbarEl.style.zIndex = "50";
            toolbarEl.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
          }
        }
      };

      const handleScroll = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(updateToolbarPosition);
      };

      const handleResize = () => {
        if (isToolbarStickyRef.current && toolbarEl) {
          const container = containerRef.current;
          if (container) {
            const containerRect = container.getBoundingClientRect();
            const containerComputedStyle = window.getComputedStyle(container);
            const containerPaddingLeft = parseInt(containerComputedStyle.paddingLeft) || 0;
            const containerPaddingRight = parseInt(containerComputedStyle.paddingRight) || 0;
            
            toolbarEl.style.left = `${containerRect.left + containerPaddingLeft}px`;
            toolbarEl.style.width = `${containerRect.width - containerPaddingLeft - containerPaddingRight}px`;
          }
        }
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      window.addEventListener("resize", handleResize, { passive: true });
      updateToolbarPosition(); // Check initial state

      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    };

    // Function to find and setup toolbar
    const findAndSetupToolbar = (): HTMLElement | null => {
      if (toolbarRef.current) {
        return toolbarRef.current;
      }
      
      if (containerRef.current) {
        const toolbarEl = containerRef.current.querySelector(".ql-toolbar") as HTMLElement | null;
        if (toolbarEl) {
          toolbarRef.current = toolbarEl;
          return toolbarEl;
        }
      }
      return null;
    };

    // Try to find toolbar immediately
    let toolbar = findAndSetupToolbar();
    const container = containerRef.current;
    
    if (!toolbar || !container) {
      // Retry after a delay
      const timeoutId = setTimeout(() => {
        const foundToolbar = findAndSetupToolbar();
        if (foundToolbar && containerRef.current) {
          setupScrollHandler(foundToolbar, containerRef.current);
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }

    return setupScrollHandler(toolbar, container);
  }, [isQuillReady, activeTab]);

  // Sync external value changes to editor
  React.useEffect(() => {
    const normalizedValue = normalizeHtml(value || "");
    
    // Update HTML content state
    setHtmlContent(normalizedValue);

    // Sync to Quill if it's ready and content changed
    if (quillRef.current && activeTab === "visual" && normalizedValue !== lastSyncedValueRef.current) {
      try {
        isUpdatingFromPropsRef.current = true;
        const delta = quillRef.current.clipboard.convert({ html: normalizedValue || "<p><br></p>" });
        quillRef.current.setContents(delta, "silent");
        lastSyncedValueRef.current = normalizedValue;
        isUpdatingFromPropsRef.current = false;
      } catch (error) {
        console.error("Failed to sync value to Quill:", error);
        isUpdatingFromPropsRef.current = false;
      }
    }
  }, [value, activeTab, normalizeHtml]);

  // Handle tab switching
  const handleTabChange = React.useCallback((newTab: string) => {
    const tabValue = newTab as "visual" | "html";
    
    if (tabValue === "html" && quillRef.current) {
      // Switching to HTML mode - capture current Quill content
      const currentHtml = quillRef.current.root.innerHTML;
      const normalized = normalizeHtml(currentHtml);
      setHtmlContent(normalized);
      lastSyncedValueRef.current = normalized;
    }
    
    setActiveTab(tabValue);
  }, [normalizeHtml]);

  // Handle HTML textarea changes
  const handleHtmlChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newHtml = e.target.value;
    const normalized = normalizeHtml(newHtml);
    setHtmlContent(normalized);
    lastSyncedValueRef.current = normalized;
    onChange(normalized);
  }, [onChange, normalizeHtml]);

  // Sync HTML content back to Quill when switching back to visual mode
  React.useEffect(() => {
    if (activeTab === "visual" && quillRef.current && isQuillReady) {
      const normalizedHtml = normalizeHtml(htmlContent || "");
      if (normalizedHtml !== lastSyncedValueRef.current) {
        try {
          isUpdatingFromPropsRef.current = true;
          const delta = quillRef.current.clipboard.convert({ html: normalizedHtml || "<p><br></p>" });
          quillRef.current.setContents(delta, "silent");
          lastSyncedValueRef.current = normalizedHtml;
          isUpdatingFromPropsRef.current = false;
        } catch (error) {
          console.error("Failed to sync HTML to Quill:", error);
          isUpdatingFromPropsRef.current = false;
        }
      }
    }
  }, [activeTab, htmlContent, isQuillReady, normalizeHtml]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      // Reset toolbar position if it's fixed
      if (toolbarRef.current && isToolbarStickyRef.current) {
        toolbarRef.current.style.position = "sticky";
        toolbarRef.current.style.top = "0";
        toolbarRef.current.style.left = "";
        toolbarRef.current.style.width = "";
        toolbarRef.current.style.zIndex = "50";
        toolbarRef.current.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
        isToolbarStickyRef.current = false;
      }

      if (quillRef.current) {
        try {
          // Remove event listeners
          quillRef.current.off("text-change");
          
          // Remove Quill instance from DOM
          const container = quillRef.current.container;
          if (container && container.parentNode) {
            container.parentNode.removeChild(container);
          }
        } catch (error) {
          console.error("Error during Quill cleanup:", error);
        }
        
        quillRef.current = null;
      }
      
      // Clear container
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
      
      isInitializingRef.current = false;
      setIsQuillReady(false);
    };
  }, []);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6">
        <label className="block text-sm font-medium text-gray-700 mb-4">
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        {initError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">
              Lỗi khởi tạo editor: {initError}
            </p>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="visual">Visual</TabsTrigger>
            <TabsTrigger value="html">HTML</TabsTrigger>
          </TabsList>
          
          <TabsContent value="visual" className="mt-4">
            {!isQuillReady && !initError && (
              <div className="flex items-center justify-center min-h-[400px] border border-gray-300 rounded-lg">
                <p className="text-sm text-gray-500">Đang tải editor...</p>
              </div>
            )}
            <div
              ref={containerRef}
              className="quill-editor-container border border-gray-300 rounded-lg"
              style={{ minHeight: "400px", display: isQuillReady ? "block" : "none" }}
            />
          </TabsContent>
          
          <TabsContent value="html" className="mt-4">
            <Textarea
              value={htmlContent}
              onChange={handleHtmlChange}
              placeholder={placeholder}
              rows={15}
              className={`font-mono text-sm resize-none ${
                error ? "border-red-500" : "border-gray-300"
              }`}
            />
          </TabsContent>
        </Tabs>

        {error && (
          <p className="text-sm text-red-500 mt-2">{error}</p>
        )}
      </div>
    </div>
  );
}

